import { Mesh, MultiMaterial, Nullable, PBRMetallicRoughnessMaterial, Plane, Ray, Scene, SubMesh, Texture, Vector3 } from "@babylonjs/core";
import { environment } from "../../environment.enum";
import { getEnvKey } from "../../environments";
import { UtilityService } from "../services/utility.service";

export class TileService {

    utilityService: UtilityService;

    tiledGround?: Mesh;

    private scene: Scene;

    private clipPlane1: Nullable<Plane> = undefined;
    private clipPlane2: Nullable<Plane> = undefined;
    private clipPlane3: Nullable<Plane> = undefined;
    private clipPlane4: Nullable<Plane> = undefined;

    constructor(scene: Scene, utilityService: UtilityService) {
        this.utilityService = utilityService;
        this.scene = scene;
    }

    hideTiledGround() {
        if (this.tiledGround) {
            this.tiledGround.isVisible = false;
            this.tiledGround.unfreezeNormals();
            this.tiledGround.unfreezeWorldMatrix();
        }
    }

    showTiledGround() {
        if (this.tiledGround) {
            this.tiledGround.isVisible = true;
            this.tiledGround.freezeNormals();
            this.tiledGround.freezeWorldMatrix();
        }
    }

    updateTiles(subdivisions: { h: number, w: number }, zoom: number, xTileBase: number, yTileBase: number, lat: number, initiallyVisible: boolean) {
        if (this.tiledGround) {
            this.tiledGround.isVisible = false;
        }
        this.createMapTiles(subdivisions, zoom, xTileBase, yTileBase, lat, initiallyVisible);
    }

    cleanClippingPlanesFromScene() {
        this.scene.clipPlane = undefined;
        this.scene.clipPlane2 = undefined;
        this.scene.clipPlane3 = undefined;
        this.scene.clipPlane4 = undefined;
    }

    assignClippingPlanesToScene() {
        this.scene.clipPlane = this.clipPlane1;
        this.scene.clipPlane2 = this.clipPlane2;
        this.scene.clipPlane3 = this.clipPlane3;
        this.scene.clipPlane4 = this.clipPlane4;
    }

    checkIntersectionAgainsTiledGround(ray: Ray) {
        return ray.intersectsMesh(this.tiledGround as any);
    }

    private createMapTiles(subdivisions: { h: number, w: number }, zoom: number, xTileBase: number, yTileBase: number, lat: number, initiallyVisible: boolean) {
        /////////////////////////// START TILES /////////////////////////////////////////////////////////////////////////////////////////////
        // adapted from: https://makina-corpus.com/blog/metier/2014/how-to-use-multimaterials-with-a-tiled-ground-in-babylonjs
        // Tiled Ground Tutorial

        // Precision - ({w: number, h: number} ) number of subdivisions on the height and the width of each tile.
        var precision = {
            "w": 1,
            "h": 1
        };
        // Subdivisions - {w: number, h: number} ) number of subdivisions (tiles) on the height and the width of the map.

        const totalGridLength = this.utilityService.metersPerPixelMath(zoom, lat) * 256 * subdivisions.h;
        const dist = totalGridLength / 2;
        let xmin = -dist;
        let zmin = -dist;
        let xmax = dist;
        let zmax = dist;

        this.clipPlane1 = new Plane(-1, 0, 0, -dist);
        this.clipPlane2 = new Plane(1, 0, 0, -dist);
        this.clipPlane3 = new Plane(0, 0, -1, -dist);
        this.clipPlane4 = new Plane(0, 0, 1, -dist);

        // Updatable - This is true if the mesh is updatable.
        // Create the Tiled Ground
        if (!this.tiledGround) {
            this.tiledGround = Mesh.CreateTiledGround("Tiled Ground", xmin, zmin, xmax, zmax, subdivisions, precision, this.scene, false);
            this.tiledGround.isPickable = false;
            this.tiledGround.position = new Vector3(0, -0.1, 0);
            this.tiledGround.receiveShadows = true;
        }

        // Part 2 : Create the multi material
        // Create differents materials

        // Create Multi Material


        // CHECK THESE
        // EXAMPLE CODE OPEN MAP TILES: https://wiki.openstreetmap.org/wiki/Tiles#Using_tiles
        //                     JS CODE: https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#ECMAScript_.28JavaScript.2FActionScript.2C_etc..29
        // CALC LAT LONG XY:            https://www.latlong.net/convert-address-to-lat-long.html
        // FIND LAT LONG ON ADDRESS:    http://oms.wff.ch/calc.htm OR CHECK URL IN gglemaps

        // to center the place, we need to add and remove half of tiles
        // TRIED THIS SECTION AS A FUNCTION 
        const tileUpdates = (myxTileBase: number) => {
            const multimat = new MultiMaterial("multi", this.scene);

            for (let row = 0; row < subdivisions.h; row++) {
                for (let col = 0; col < subdivisions.w; col++) {

                    const material = new PBRMetallicRoughnessMaterial(
                        "material" + row + "-" + col,
                        this.scene
                    );

                    const texture = new Texture(
                        `https://api.maptiler.com/maps/nl-cartiqo-light/256/${zoom}/${myxTileBase + col}/${yTileBase - row}@2x.png?key=${getEnvKey((process.env.REACT_APP_ENVIRONMENT as environment), 'maptilerKey')}`,
                        this.scene
                    );
                    texture.level = 1;

                    material.baseTexture = texture;
                    material.metallic = 0;
                    material.roughness = 1;
                    material._environmentIntensity = 0.3;

                    material.baseTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
                    material.baseTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
                    multimat.subMaterials.push(material);
                }
            }

            // Part 3 : Apply the multi material
            // Define multimat as material of the tiled ground
            this.tiledGround.material = multimat;

            // Needed variables to set subMeshes
            var verticesCount = this.tiledGround.getTotalVertices();
            var tileIndicesLength = this.tiledGround.getIndices().length / (subdivisions.w * subdivisions.h);

            // Set subMeshes of the tiled ground
            this.tiledGround.subMeshes = [];
            var index = 0;
            var base = 0;
            for (let row = 0; row < subdivisions.h; row++) {
                for (let col = 0; col < subdivisions.w; col++) {
                    var submesh = new SubMesh(
                        index++, 0, verticesCount, base, tileIndicesLength, this.tiledGround
                    );

                    this.tiledGround.subMeshes.push(submesh);
                    base += tileIndicesLength;
                }
            }

            this.tiledGround.scaling.x = -1;
            this.tiledGround.isVisible = initiallyVisible;
            this.tiledGround.freezeNormals();
            this.tiledGround.freezeWorldMatrix();
        }
        // SHOW TILES
        tileUpdates(xTileBase);
    }
}